import React, { useEffect, useRef, useState } from 'react';

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  /** One value per label; null leaves a gap */
  values: (number | null)[];
  dashed?: boolean;
  /** Plotted against the right-hand axis, in its own unit; drawn thinner, with a dot on each value */
  axis?: 'right';
}

interface LineChartProps {
  labels: string[];
  series: ChartSeries[];
  /** Axis ticks (compact amounts) */
  formatValue: (value: number) => string;
  /** Right-hand axis ticks, for series on the right axis */
  formatRightValue?: (value: number) => string;
  /** Titles written along each axis, e.g. its unit */
  leftAxisTitle?: string;
  rightAxisTitle?: string;
  /** The readout for one point: every series at that label */
  renderTooltip: (index: number) => React.ReactNode;
  /** A vertical marker at this point, e.g. today */
  markerIndex?: number;
  markerLabel?: string;
  ariaLabel: string;
  /** Pixels; 260 by default */
  height?: number;
}

const MARGIN = { top: 16, right: 28, bottom: 28, left: 60 };
const RIGHT_AXIS_MARGIN = 48;
/** Room for an axis title, written sideways along the axis */
const AXIS_TITLE = 18;
const TICKS = [0, 1, 2, 3, 4];

/** The smallest of 1, 2, 2.5 or 5 times a power of ten at or above `value` */
function niceStep(value: number): number {
  if (value <= 0) {
    return 1;
  }
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * power >= value) ?? 10;
  return step * power;
}

/** Splits values into runs without gaps, as [index, value] pairs */
function runs(values: (number | null)[]): [number, number][][] {
  const all: [number, number][][] = [];
  let current: [number, number][] = [];
  values.forEach((value, i) => {
    if (value === null) {
      if (current.length > 0) all.push(current);
      current = [];
    } else {
      current.push([i, value]);
    }
  });
  if (current.length > 0) all.push(current);
  return all;
}

/** The axis for these series: 4 even steps from 0 to a round number above their largest value */
function scaleOf(series: ChartSeries[]): number {
  const max = Math.max(
    0,
    ...series.flatMap((s) => s.values.filter((v): v is number => v !== null))
  );
  return niceStep(max / 4);
}

/**
 * A line chart from 0, sized to its container, with a legend, a crosshair that snaps to the nearest label (by
 * pointer or arrow keys) and a table for screen readers. Series in another unit can go on a right-hand axis.
 */
export const LineChart: React.FC<LineChartProps> = ({
  labels,
  series,
  formatValue,
  formatRightValue = formatValue,
  leftAxisTitle,
  rightAxisTitle,
  renderTooltip,
  markerIndex,
  markerLabel,
  ariaLabel,
  height: HEIGHT = 260,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, entry.contentRect.width))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const hasRight = series.some((s) => s.axis === 'right');
  const marginLeft = MARGIN.left + (leftAxisTitle ? AXIS_TITLE : 0);
  const marginRight =
    (hasRight ? RIGHT_AXIS_MARGIN : MARGIN.right) +
    (hasRight && rightAxisTitle ? AXIS_TITLE : 0);
  const plotWidth = width - marginLeft - marginRight;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const count = labels.length;
  const x = (i: number) =>
    marginLeft + (count > 1 ? (i * plotWidth) / (count - 1) : plotWidth / 2);
  const step = scaleOf(series.filter((s) => s.axis !== 'right'));
  const rightStep = scaleOf(series.filter((s) => s.axis === 'right'));
  const y = (value: number, axis?: 'right') =>
    MARGIN.top +
    plotHeight -
    (value / ((axis === 'right' ? rightStep : step) * 4)) * plotHeight;
  const labelEvery = Math.max(1, Math.ceil(count / Math.floor(plotWidth / 56)));

  const pointAt = (clientX: number, svg: SVGSVGElement) => {
    const left = svg.getBoundingClientRect().left;
    const i = Math.round(
      ((clientX - left - marginLeft) / plotWidth) * (count - 1)
    );
    return Math.min(count - 1, Math.max(0, i));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      setHover((h) =>
        Math.min(
          count - 1,
          Math.max(0, (h ?? (delta > 0 ? -1 : count)) + delta)
        )
      );
    }
  };

  return (
    <div>
      {series.length > 1 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2 text-sm text-gray-600">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-2">
              <svg width="20" height="4" aria-hidden="true">
                <line
                  x1="0"
                  y1="2"
                  x2="20"
                  y2="2"
                  stroke={s.color}
                  strokeWidth={s.axis === 'right' ? 1.5 : 2}
                  strokeDasharray={s.dashed ? '4 3' : undefined}
                />
                {s.axis === 'right' && (
                  <circle cx="10" cy="2" r="2" fill={s.color} />
                )}
              </svg>
              {s.label}
            </span>
          ))}
        </div>
      )}
      <div ref={containerRef} className="relative w-full">
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          onPointerMove={(e) => setHover(pointAt(e.clientX, e.currentTarget))}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setHover(null)}
        >
          {TICKS.map((tick) => (
            <g key={tick}>
              <line
                x1={marginLeft}
                x2={width - marginRight}
                y1={y(tick * step)}
                y2={y(tick * step)}
                stroke={tick === 0 ? '#d1d5db' : '#f0f0ee'}
              />
              <text
                x={marginLeft - 8}
                y={y(tick * step)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-gray-500 text-[11px]"
              >
                {formatValue(tick * step)}
              </text>
              {hasRight && (
                <text
                  x={width - marginRight + 8}
                  y={y(tick * rightStep, 'right')}
                  dominantBaseline="middle"
                  className="fill-gray-500 text-[11px]"
                >
                  {formatRightValue(tick * rightStep)}
                </text>
              )}
            </g>
          ))}
          {leftAxisTitle && (
            <text
              transform={`translate(12 ${MARGIN.top + plotHeight / 2}) rotate(-90)`}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-600 text-[11px] font-medium"
            >
              {leftAxisTitle}
            </text>
          )}
          {hasRight && rightAxisTitle && (
            <text
              transform={`translate(${width - 10} ${MARGIN.top + plotHeight / 2}) rotate(90)`}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-gray-600 text-[11px] font-medium"
            >
              {rightAxisTitle}
            </text>
          )}
          {labels.map((label, i) =>
            i % labelEvery === 0 ? (
              <text
                key={i}
                x={x(i)}
                y={HEIGHT - 8}
                textAnchor="middle"
                className="fill-gray-500 text-[11px]"
              >
                {label}
              </text>
            ) : null
          )}
          {markerIndex !== undefined && (
            <g>
              <line
                x1={x(markerIndex)}
                x2={x(markerIndex)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke="#9ca3af"
                strokeDasharray="2 3"
              />
              <text
                x={x(markerIndex) + 4}
                y={MARGIN.top + 2}
                dominantBaseline="hanging"
                className="fill-gray-600 text-[11px] font-medium"
              >
                {markerLabel}
              </text>
            </g>
          )}
          {series.map((s) =>
            runs(s.values).map((run, r) => (
              <g key={`${s.key}-${r}`}>
                {run.length > 1 && (
                  <path
                    d={run
                      .map(
                        ([i, v], j) =>
                          `${j === 0 ? 'M' : 'L'}${x(i)},${y(v, s.axis)}`
                      )
                      .join(' ')}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={s.axis === 'right' ? 1.5 : 2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={s.dashed ? '6 4' : undefined}
                  />
                )}
                {(run.length === 1 || s.axis === 'right') &&
                  run.map(([i, v]) => (
                    <circle
                      key={i}
                      cx={x(i)}
                      cy={y(v, s.axis)}
                      r={run.length === 1 ? 4 : 2.5}
                      fill={s.color}
                    />
                  ))}
              </g>
            ))
          )}
          {hover !== null && (
            <g pointerEvents="none">
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                stroke="#6b7280"
              />
              {series.map((s) =>
                s.values[hover] === null ? null : (
                  <circle
                    key={s.key}
                    cx={x(hover)}
                    cy={y(s.values[hover] as number, s.axis)}
                    r={4}
                    fill={s.color}
                    stroke="#ffffff"
                    strokeWidth={2}
                  />
                )
              )}
            </g>
          )}
        </svg>
        {hover !== null && (
          <div
            className="absolute top-2 z-10 pointer-events-none bg-white border border-gray-200 shadow-md rounded-md px-3 py-2 text-sm min-w-[12rem]"
            style={
              x(hover) > width / 2
                ? { right: width - x(hover) + 12 }
                : { left: x(hover) + 12 }
            }
          >
            <div className="font-medium text-gray-900 mb-1">
              {labels[hover]}
            </div>
            {renderTooltip(hover)}
          </div>
        )}
      </div>
      {/* Hidden from view, for screen readers; a table itself ignores the 1px box, so it sits in a div */}
      <div className="sr-only">
        <table>
          <caption>{ariaLabel}</caption>
          <thead>
            <tr>
              <th scope="col" />
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map((label, i) => (
              <tr key={i}>
                <th scope="row">{label}</th>
                {series.map((s) => (
                  <td key={s.key}>
                    {s.values[i] === null
                      ? ''
                      : (s.axis === 'right' ? formatRightValue : formatValue)(
                          s.values[i] as number
                        )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** A tooltip row: a short line in the series' color, the value first, then its name */
export const TooltipRow: React.FC<{
  color?: string;
  dashed?: boolean;
  value: string;
  label: string;
}> = ({ color, dashed, value, label }) => (
  <div className="flex items-center gap-2 py-0.5">
    <svg width="14" height="4" aria-hidden="true" className="flex-shrink-0">
      {color && (
        <line
          x1="0"
          y1="2"
          x2="14"
          y2="2"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={dashed ? '3 2' : undefined}
        />
      )}
    </svg>
    <span className="font-semibold text-gray-900 tabular-nums">{value}</span>
    <span className="text-gray-500">{label}</span>
  </div>
);
